import { useCallback, useEffect, useRef, useState } from 'react';
import { getSttGrpcToken } from '@services/chatService';
import {
  CHAT_SESSION_STORAGE_KEY,
  STT_DEVICE_TYPE_WEB,
  STT_PROVIDER,
  type SttProvider,
} from '@constants/speechToText';

interface UseSttProviderReturn {
  /** Model STT đang dùng: VIETTEL → gRPC Viettel, DEFAULT_SDK → STT của trình duyệt. */
  provider: SttProvider;
  /** Đã nhận được cấu hình từ BE (false: đang dùng mặc định hoặc lần gọi gần nhất bị lỗi). */
  isLoaded: boolean;
  refresh: () => Promise<void>;
}

/**
 * Lấy provider STT của web từ BE (deviceType=web). Mặc định/lỗi mạng/token chưa có → DEFAULT_SDK
 * (STT trình duyệt như trước) để mic luôn dùng được; chỉ chuyển sang gRPC khi BE trả VIETTEL.
 * Tự gọi lại khi token đổi.
 */
export const useSttProvider = (token?: string | null): UseSttProviderReturn => {
  const [provider, setProvider] = useState<SttProvider>(STT_PROVIDER.DEFAULT_SDK);
  const [isLoaded, setIsLoaded] = useState(false);

  const tokenRef = useRef(token);
  tokenRef.current = token;
  // Chỉ nhận kết quả của lần gọi mới nhất (token đổi / refresh chồng nhau)
  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    const accessToken = tokenRef.current || sessionStorage.getItem(CHAT_SESSION_STORAGE_KEY.ACCESS_TOKEN);
    if (!accessToken) {
      console.log('[STT] No access token yet → using default STT (browser) until provider is loaded');
      return;
    }

    const requestId = ++requestIdRef.current;
    try {
      const config = await getSttGrpcToken(accessToken, STT_DEVICE_TYPE_WEB);
      if (requestId !== requestIdRef.current) return;
      const rawProvider = String(config?.provider ?? '').trim();
      const isViettel = rawProvider.toUpperCase() === STT_PROVIDER.VIETTEL;
      const nextProvider = isViettel ? STT_PROVIDER.VIETTEL : STT_PROVIDER.DEFAULT_SDK;
      // Không log accessToken/thông tin kết nối trong response
      console.log(
        `[STT] Provider from API (deviceType=${STT_DEVICE_TYPE_WEB}): ${rawProvider || '(empty)'} → ` +
          (isViettel
            ? 'use gRPC Viettel'
            : `use browser STT (Web Speech API)${rawProvider.toUpperCase() === STT_PROVIDER.DEFAULT_SDK ? '' : ' [unknown provider, fallback]'}`),
      );
      setProvider(nextProvider);
      setIsLoaded(true);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      // Chỉ log message: AxiosError đầy đủ chứa header Authorization
      console.warn(
        '[STT] Failed to fetch STT provider → fallback to browser STT (Web Speech API):',
        error instanceof Error ? error.message : String(error),
      );
      setProvider(STT_PROVIDER.DEFAULT_SDK);
      setIsLoaded(false);
    }
  }, []);

  useEffect(() => {
    setProvider(STT_PROVIDER.DEFAULT_SDK);
    setIsLoaded(false);
    void refresh();
  }, [token, refresh]);

  return { provider, isLoaded, refresh };
};

export default useSttProvider;
